'use client';

import { handle_GTM_33 } from '@gtm/33';
import { handle_GTM_34 } from '@gtm/34';
import { handle_GTM_52 } from '@gtm/52';
import { handle_GTM_64 } from '@gtm/64';
import { handle_GTM_69 } from '@gtm/69';
import GloLayout from 'code-micro/common/GloLayoutA';
import { ContentServerProps } from 'code-micro/props/content';
import { useIntersectionObservers } from 'eco-design-micro/hooks/useIntersectionObservers';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/navigation';
import { Swiper as SwiperType } from 'swiper/types';
import { defaultChild } from '../setting/config';
import { ItfProductFloorBtn, ItfProductFloorChild, ItfProductFloorData, ItfProductFloorItem, ItfProductFloorLink } from '../setting/type';
import Arrow from './icons';
import './index.scss';

interface ItfAniArrItem {
    name: string;
    cls: string;
}

const SWIPER_SPACE = 12;
const SWIPER_EDGE_TOLERANCE = SWIPER_SPACE * 4;

type SwiperWithDom = SwiperType & {
    el?: HTMLElement;
    slides?: HTMLElement[] | ArrayLike<HTMLElement>;
    snapGrid?: number[];
    speed?: number;
    navigation?: {
        update?: () => void;
    };
    setTransition?: (duration: number) => void;
    updateActiveIndex?: () => void;
    updateSlidesClasses?: () => void;
};

const setSwiperTranslate = (swiperIns: SwiperType, translate: number, duration = 0) => {
    const swiperWithDom = swiperIns as SwiperWithDom;

    swiperWithDom.setTransition?.(duration);
    swiperIns.setTranslate(translate);
    swiperIns.updateProgress();
    swiperWithDom.updateActiveIndex?.();
    swiperWithDom.updateSlidesClasses?.();
    swiperWithDom.navigation?.update?.();
};

const getRealMaxTranslate = (swiperIns: SwiperType) => {
    const swiperWithDom = swiperIns as SwiperWithDom;
    const swiperEl = swiperWithDom.el;
    const slides = swiperWithDom.slides ? Array.from(swiperWithDom.slides) : [];
    const lastSlide = slides[slides.length - 1];
    const containerWidth = swiperEl?.clientWidth || swiperIns.width;
    const contentWidth = lastSlide ? lastSlide.offsetLeft + lastSlide.offsetWidth : 0;

    if (!contentWidth || contentWidth <= containerWidth) return 0;

    return -(contentWidth - containerWidth);
};

const setButtonDisabled = (button: Element | null, disabled: boolean) => {
    if (!button) return;

    button.classList.toggle('swiper-button-disabled', disabled);
    button.setAttribute('aria-disabled', `${disabled}`);
};

const updateSwiperNavigationState = (swiperIns: SwiperType, position: 'start' | 'middle' | 'end' | 'locked') => {
    const swiperEl = (swiperIns as SwiperWithDom).el;
    const prev = swiperEl?.querySelector('.swiper-button-prev') || null;
    const next = swiperEl?.querySelector('.swiper-button-next') || null;

    setButtonDisabled(prev, position === 'start' || position === 'locked');
    setButtonDisabled(next, position === 'end' || position === 'locked');
};

const syncSwiperEdge = (swiperIns: SwiperType) => {
    const maxTranslate = getRealMaxTranslate(swiperIns);
    const minTranslate = 0;
    const distanceToEnd = Math.abs(swiperIns.translate - maxTranslate);

    if (maxTranslate === minTranslate) {
        setSwiperTranslate(swiperIns, minTranslate);
        updateSwiperNavigationState(swiperIns, 'locked');
        return 'start';
    }

    if (swiperIns.translate > minTranslate) {
        setSwiperTranslate(swiperIns, minTranslate);
        updateSwiperNavigationState(swiperIns, 'start');
        return 'start';
    }

    if (swiperIns.translate < maxTranslate || distanceToEnd <= SWIPER_EDGE_TOLERANCE) {
        setSwiperTranslate(swiperIns, maxTranslate);
        updateSwiperNavigationState(swiperIns, 'end');
        return 'end';
    }

    updateSwiperNavigationState(swiperIns, 'middle');
    return 'middle';
};

const shouldAnimateNextToRealEnd = (swiperIns: SwiperType) => {
    const swiperWithDom = swiperIns as SwiperWithDom;
    const maxTranslate = getRealMaxTranslate(swiperIns);
    if (maxTranslate === 0) return true;

    const currentOffset = -swiperIns.translate;
    const realEndOffset = -maxTranslate;
    const nextSnap = swiperWithDom.snapGrid?.find((snap) => snap > currentOffset + 1);

    return typeof nextSnap === 'number' && nextSnap >= realEndOffset - SWIPER_EDGE_TOLERANCE;
};

const animateToRealEnd = (swiperIns: SwiperType) => {
    const swiperWithDom = swiperIns as SwiperWithDom;
    const maxTranslate = getRealMaxTranslate(swiperIns);

    setSwiperTranslate(swiperIns, maxTranslate, swiperWithDom.speed || 300);
    updateSwiperNavigationState(swiperIns, 'end');
};

export default function Content({ id, data = {}, isEditStatus }: ContentServerProps) {
    const { theme, title, titleColor, desc, descColor, children } = data as ItfProductFloorData;
    const [child, setChild] = useState<ItfProductFloorChild>(children?.[0] || (defaultChild as ItfProductFloorChild));
    const prefixCls = 'product-floor';
    const [isH5, setIsH5] = useState<boolean>(false);
    const [swiper, setSwiper] = useState<SwiperType | null>(null);
    const swiperRef = useRef<any>(null);
    const [enter, setEnter] = useState({
        title: isEditStatus,
        desc: isEditStatus,
        scene: isEditStatus,
        items: isEditStatus,
    });

    const renderList = useMemo(() => {
        return (
            <div className={`${prefixCls}-links`}>
                {child?.links?.map(
                    (link: ItfProductFloorLink, index: number) =>
                        link?.url &&
                        link?.text && (
                            <EcoLink
                                className={`${prefixCls}-links-link GtmModuleData-btn-href`}
                                key={index}
                                href={link?.url}
                                onClick={() => handle_GTM_Event(link, title)}
                            >
                                <span className={`${prefixCls}-links-link-text`}>{link.text}</span>
                                <Arrow />
                            </EcoLink>
                        ),
                )}
            </div>
        );
    }, [child]);

    const renderItems = useMemo(() => {
        return (
            <>
                {child?.items?.map((item: ItfProductFloorItem, j: number) => (
                    <SwiperSlide
                        className={`${prefixCls}-items-items-swiper-slide ${enter?.items ? 'enter' : ''}`}
                        key={`${JSON.stringify(item)}_${j}}`}
                        style={{ transitionDelay: enter?.items ? `${(j + (isH5 ? 0 : 1)) * 0.15}s` : '0s' }}
                    >
                        {item?.label && (
                            <span className={`${prefixCls}-items-items-swiper-slide-label`} style={{ color: item?.labelColor }}>
                                {item?.label}
                            </span>
                        )}
                        <div className={`${prefixCls}-items-items-swiper-slide-img ${item?.hoverImg ? 'has-hover' : 'no-hover'}`}>
                            <EcoImg className={`${prefixCls}-items-items-swiper-slide-img-product`} pc={item?.img} alt={item.name} aria-label={item.name} />
                            {item?.hoverImg && (
                                <EcoImg
                                    className={`${prefixCls}-items-items-swiper-slide-img-scene`}
                                    pc={item?.hoverImg}
                                    h5={item?.hoverImgH5}
                                    alt={item.name}
                                    aria-label={item.name}
                                />
                            )}
                        </div>
                        <div className={`${prefixCls}-items-items-swiper-slide-info`}>
                            {item?.name && (
                                <p className={`${prefixCls}-items-items-swiper-slide-name`} title={item?.name}>
                                    {item?.name}
                                </p>
                            )}
                            {item?.text && (
                                <p className={`${prefixCls}-items-items-swiper-slide-text`} title={item?.text}>
                                    {item?.text}
                                </p>
                            )}
                            {item?.btns?.length > 0 && (
                                <div className={`${prefixCls}-items-items-swiper-slide-btns ${prefixCls}-btns btns-${item?.btnsType || 'X'}`}>
                                    {item.btns?.map(
                                        (btn: ItfProductFloorBtn, i: number) =>
                                            btn?.url &&
                                            btn?.text && (
                                                <EcoLink
                                                    className={`${prefixCls}-items-items-swiper-slide-btns-btn ${prefixCls}-btns-btn GtmModuleData-btn-href type-${btn.type}`}
                                                    href={btn.url}
                                                    onClick={() => handle_GTM_Event(btn, item?.name)}
                                                    key={`${j}-${i}`}
                                                >
                                                    <span>{btn.text}</span>
                                                    {btn?.type === 'text' && <Arrow />}
                                                </EcoLink>
                                            ),
                                    )}
                                </div>
                            )}
                        </div>
                    </SwiperSlide>
                ))}
            </>
        );
    }, [enter?.items, isH5, child]);

    const arr: ItfAniArrItem[] = [
        { name: 'title', cls: `${prefixCls}-info-title` },
        { name: 'desc', cls: `${prefixCls}-info-desc` },
        { name: 'scene', cls: `${prefixCls}-items-scene-img` },
        { name: 'items', cls: `${prefixCls}-items-items-swiper-slide-img` },
    ];

    useIntersectionObservers(
        {
            selectors: [
                `.editor-${prefixCls}-${id} .${prefixCls}-info-title`,
                `.editor-${prefixCls}-${id} .${prefixCls}-info-desc`,
                `.editor-${prefixCls}-${id} .${prefixCls}-items-scene-img`,
                `.editor-${prefixCls}-${id} .${prefixCls}-items-items-swiper-slide-img`,
            ],
            onEnter: (e: Element) => {
                const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
                if (aim) {
                    setEnter((prev) => ({ ...prev, [aim.name]: true }));
                }
            },
            // onLeave: (e: Element) => {
            //     const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
            //     if (aim) {
            //         setEnter((prev) => ({ ...prev, [aim.name]: false }));
            //     }
            // },
        },
        [isH5, data],
        isEditStatus,
    );

    const handle_GTM_Event = (btn: ItfProductFloorBtn | ItfProductFloorLink, name: string) => {
        const { url, text } = btn;
        // 1. checkout => https://us.ecoflow.com/checkouts/cn/hWN6bOi6WvZ1r1ECVcafOoKO/en-us?_r=AQABFnGHZn-cZ1lZW7AM6Pa19wIXIWn4XUcLF8yGzrkL
        const regex1 = /^https:\/\/[a-z]{2}\.ecoflow\.com\/(?:cart|checkouts)\//i;
        const regex2 = /^https:\/\/[a-z]{2}\.ecoflow\.com\//i;
        const regex3 = /^(https:\/\/)?www\.ecoflow\.com\/[a-z]{2}\//i;
        if (regex1.test(url)) {
            // 1. checkout shopify结账页
            handle_GTM_34({
                button_name: `${text}_${name}`,
                __btn_id1: `editor-${prefixCls}-${id}`,
                __btn_id2: url,
                __btn_type: 'href',
            });
        } else if (regex2.test(url)) {
            // 2. shopify 其他页
            handle_GTM_33({
                product_name: name,
                position: `Series_ProductFloor_${id}`,
                __btn_id1: `editor-${prefixCls}-${id}`,
                __btn_id2: url,
                __btn_type: 'href',
            });
        } else if (regex3.test(url)) {
            // 3. 官网网页
            handle_GTM_52({
                list_name: title,
                product_name: name,
                button_name: text,
                position: `Series_ProductFloor_${id}`,
                __btn_id1: `editor-${prefixCls}-${id}`,
                __btn_id2: url,
                __btn_type: 'href',
            });
        } else {
            // 4. 其他
            handle_GTM_64({
                list_name: title,
                button_name: text,
                position: `Series_ProductFloor_${id}`,
                __btn_id1: `editor-${prefixCls}-${id}`,
                __btn_id2: url,
                __btn_type: 'href',
            });
        }
    };

    const handleGTM = (btn: string) => {
        handle_GTM_69({
            button_name: btn,
            position: `Series_ProductFloor-${id}`,
            __btn_id1: `editor-${prefixCls}-${id}`,
            __btn_id2: btn,
            __btn_type: 'common',
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const handleResize = () => setIsH5(window.innerWidth < 768);

            handleResize();
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    useEffect(() => {
        if (swiper) {
            swiper.allowTouchMove = !isH5;
        }
    }, [isH5, swiper]);

    useEffect(() => {
        if (!swiper) return;

        const frame = window.requestAnimationFrame(() => {
            swiper.update();
            syncSwiperEdge(swiper);
        });

        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [swiper, child?.items, isH5]);

    useEffect(() => {
        setChild(children?.[0] || defaultChild);
    }, [data]);

    useEffect(() => {
        if (swiperRef.current && swiper) {
            const prev = swiperRef.current.querySelector('.swiper-button-prev');
            const next = swiperRef.current.querySelector('.swiper-button-next');
            const handlePrevClick = (event: Event) => {
                handleGTM('<');
                if (syncSwiperEdge(swiper) === 'start') {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            };
            const handleNextClick = (event: Event) => {
                handleGTM('>');
                if (syncSwiperEdge(swiper) === 'end') {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    return;
                }

                if (shouldAnimateNextToRealEnd(swiper)) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    animateToRealEnd(swiper);
                }
            };

            if (prev) {
                prev.classList.add('GtmModuleData-btn-common');
                prev.setAttribute('data-btn-id', '<');
                prev.addEventListener('click', handlePrevClick, true);
            }
            if (next) {
                next.classList.add('GtmModuleData-btn-common');
                next.setAttribute('data-btn-id', '>');
                next.addEventListener('click', handleNextClick, true);
            }
            return () => {
                if (prev) {
                    prev.removeEventListener('click', handlePrevClick, true);
                }
                if (next) {
                    next.removeEventListener('click', handleNextClick, true);
                }
            };
        }
    }, [swiperRef.current, swiper, child?.items]);

    return (
        <GloLayout className={`editor-${prefixCls} editor-${prefixCls}-${id} theme-${theme} isH5-${isH5}`} id={id} data={data} floorName={prefixCls}>
            <div className={`${prefixCls}-header`}>
                <div className={`${prefixCls}-info`}>
                    {title && (
                        <div className={`${prefixCls}-info-title ${enter?.title ? 'enter' : ''}`} style={{ color: titleColor }} title={title}>
                            {title}
                        </div>
                    )}
                    {desc && (
                        <div className={`${prefixCls}-info-desc ${enter?.desc ? 'enter' : ''}`} style={{ color: descColor }} title={desc}>
                            {desc}
                        </div>
                    )}
                </div>
                {renderList}
            </div>
            <div className={`${prefixCls}-items-box`} ref={swiperRef}>
                <Swiper
                    className={`${prefixCls}-items`}
                    initialSlide={0}
                    spaceBetween={SWIPER_SPACE}
                    slidesPerView={'auto'}
                    roundLengths
                    watchOverflow
                    observer
                    observeParents
                    allowTouchMove={isH5 ? false : true}
                    onSwiper={(swiperIns: SwiperType) => {
                        setSwiper(swiperIns);
                        window.requestAnimationFrame(() => syncSwiperEdge(swiperIns));
                    }}
                    onReachEnd={syncSwiperEdge}
                    onSlideChangeTransitionEnd={syncSwiperEdge}
                    onTransitionEnd={syncSwiperEdge}
                    onResize={(swiperIns: SwiperType) => {
                        swiperIns.update();
                        syncSwiperEdge(swiperIns);
                    }}
                    modules={[Navigation]}
                    navigation={true}
                >
                    <SwiperSlide className={`${prefixCls}-items-scene ${enter?.scene ? 'enter' : ''}`}>
                        <div className={`${prefixCls}-items-scene-img`}>
                            <EcoImg pc={child?.pc} h5={child?.h5} alt={child?.name} aria-label={child?.name} />
                        </div>
                        {child?.label && (
                            <p className={`${prefixCls}-items-scene-label`} style={{ color: child.labelColor }}>
                                {child?.label}
                            </p>
                        )}
                        {((child?.label && isEditStatus) ||
                            child?.name ||
                            child?.text ||
                            child?.btns?.some((btn: ItfProductFloorBtn) => btn?.text && btn?.url)) && (
                            <div className={`${prefixCls}-items-scene-info`}>
                                {child?.label && (
                                    <p className={`${prefixCls}-items-scene-info-label`} style={{ color: child.labelColor }}>
                                        {child?.label}
                                    </p>
                                )}
                                {child?.name && (
                                    <p className={`${prefixCls}-items-scene-info-name`} style={{ color: child?.nameColor }} title={child?.name}>
                                        {child?.name}
                                    </p>
                                )}
                                {child?.text && (
                                    <p className={`${prefixCls}-items-scene-info-text`} style={{ color: child?.textColor }} title={child?.text}>
                                        {child?.text}
                                    </p>
                                )}
                                {child?.btns?.length > 0 && (
                                    <div className={`${prefixCls}-items-scene-info-btns ${prefixCls}-btns`}>
                                        {child.btns?.map(
                                            (btn: ItfProductFloorBtn, i: number) =>
                                                btn?.url &&
                                                btn?.text && (
                                                    <EcoLink
                                                        className={`${prefixCls}-items-scene-info-btns-btn ${prefixCls}-btns-btn GtmModuleData-btn-href type-${btn.type}`}
                                                        href={btn.url}
                                                        onClick={() => handle_GTM_Event(btn, child?.name)}
                                                        key={i}
                                                        style={
                                                            ['fill'].includes(btn?.type)
                                                                ? {
                                                                      color: btn?.color,
                                                                      background: btn?.bgColor,
                                                                      borderColor: btn?.bgColor,
                                                                  }
                                                                : ['outline'].includes(btn?.type)
                                                                ? { color: btn?.color, borderColor: btn?.color }
                                                                : { color: btn?.color }
                                                        }
                                                    >
                                                        <span>{btn.text}</span>
                                                        {btn?.type === 'text' && <Arrow />}
                                                    </EcoLink>
                                                ),
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </SwiperSlide>
                    {isH5 ? (
                        <SwiperSlide className={`${prefixCls}-items-items`}>
                            <Swiper className={`${prefixCls}-items-items-swiper`} initialSlide={0} spaceBetween={SWIPER_SPACE} slidesPerView={'auto'}>
                                {renderItems}
                            </Swiper>
                        </SwiperSlide>
                    ) : (
                        renderItems
                    )}
                </Swiper>
            </div>
            <div className="h5-links">{renderList}</div>
        </GloLayout>
    );
}
